import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Progress } from '../ui/progress';
import { Separator } from '../ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Star,
  MapPin,
  Clock,
  DollarSign,
  Briefcase,
  GraduationCap,
  Award,
  FileText,
  Download,
  MessageSquare,
  Bookmark,
  Send,
  CheckCircle,
  Calendar,
  Phone,
  Mail,
  Shield,
  TrendingUp,
  Users,
  Target,
  Sparkles,
  ExternalLink
} from 'lucide-react';

export function CandidateProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isShortlisted, setIsShortlisted] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  // Mock candidate data - in production this would come from an API
  const candidate = {
    id: 1,
    name: 'Sarah Johnson',
    title: 'Senior Care Assistant',
    location: 'Stevenage, UK',
    experience: '7 years',
    aiScore: 94,
    skills: ['Patient Care', 'First Aid', 'Medication Management', 'Team Leadership', 'Dementia Care', 'Mental Health Support'],
    availability: 'Immediate',
    salary: '£28,000 - £32,000',
    email: 'sarah.johnson@email.com',
    phone: '+44 7700 900123',
    summary: 'Highly experienced care professional with proven track record in elderly care. Excellent communication skills and compassionate approach. Passionate about providing dignified, person-centered care.',
    matchReasons: [
      { reason: 'Location match (Stevenage)', score: 100 },
      { reason: '7 years experience in care', score: 95 },
      { reason: 'Certified in First Aid', score: 90 },
      { reason: 'Available immediately', score: 92 }
    ],
    workHistory: [
      {
        role: 'Senior Care Assistant',
        company: 'Sunrise Care Home',
        location: 'Stevenage',
        duration: 'Jan 2020 - Present',
        description: 'Leading a team of 5 care assistants, providing comprehensive care to elderly residents with various needs including dementia, mobility issues, and chronic conditions.'
      },
      {
        role: 'Care Assistant',
        company: 'Hope Valley Nursing Home',
        location: 'Hitchin',
        duration: 'Mar 2017 - Dec 2019',
        description: 'Provided personal care, administered medications, and supported residents with daily living activities. Received Employee of the Year award in 2018.'
      },
      {
        role: 'Healthcare Support Worker',
        company: 'Lister Hospital',
        location: 'Stevenage',
        duration: 'Jun 2015 - Feb 2017',
        description: 'Assisted nursing staff with patient care, vital sign monitoring, and documentation. Specialized in elderly care ward.'
      }
    ],
    education: [
      {
        qualification: 'NVQ Level 3 in Health & Social Care',
        institution: 'North Hertfordshire College',
        year: '2016'
      },
      {
        qualification: 'NVQ Level 2 in Health & Social Care',
        institution: 'North Hertfordshire College',
        year: '2015'
      }
    ],
    certifications: [
      { name: 'First Aid at Work', issuer: 'St John Ambulance', expiry: 'Valid until Dec 2025' },
      { name: 'Dementia Care Specialist', issuer: 'Alzheimer\'s Society', expiry: 'Valid until Mar 2026' },
      { name: 'Manual Handling', issuer: 'Care Training UK', expiry: 'Valid until Jun 2025' },
      { name: 'Medication Administration', issuer: 'Skills for Care', expiry: 'Valid until Sep 2025' }
    ],
    testScores: [
      { test: 'Care Skills Assessment', score: 92, percentile: 95 },
      { test: 'Communication Skills', score: 88, percentile: 90 },
      { test: 'Situational Judgment', score: 85, percentile: 87 }
    ],
    references: [
      {
        name: 'Margaret Thompson',
        role: 'Care Home Manager',
        company: 'Sunrise Care Home',
        relationship: 'Direct Supervisor',
        available: true
      },
      {
        name: 'Dr. James Roberts',
        role: 'Medical Director',
        company: 'Hope Valley Nursing Home',
        relationship: 'Previous Employer',
        available: true
      }
    ],
    preferences: {
      workType: 'Full-time',
      shiftPreference: 'Day shifts preferred, flexible',
      noticePeriod: 'Immediate',
      willingToRelocate: false,
      drivingLicense: true,
      ownTransport: true
    }
  };

  const toggleShortlist = () => {
    const previous = isShortlisted;
    const next = !isShortlisted;
    setIsShortlisted(next);
    toast.success(next ? 'Added to shortlist!' : 'Removed from shortlist', {
      duration: 4000,
      action: {
        label: 'Undo',
        onClick: () => setIsShortlisted(previous),
      },
    });
  };

  const sendMessage = () => {
    if (messageText.trim()) {
      toast.success('Message sent successfully!');
      setMessageText('');
    }
  };

  const downloadCV = () => {
    toast.success('CV downloaded successfully!');
  };

  return (
    <div className="min-h-screen bg-[#1a1d2e]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#1a1d2e]/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/employer/dashboard')}
                className="text-white hover:bg-white/5"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-white">Candidate Profile</h1>
                <p className="text-sm text-gray-400">Review full candidate information</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={toggleShortlist}
                className={isShortlisted 
                  ? "border-[#d2ac47] bg-[#ba9036]/10 text-[#ba9036] hover:bg-[#ba9036]/20" 
                  : "border-white/20 text-white hover:bg-white/5"
                }
              >
                <Bookmark className={`w-4 h-4 mr-2 ${isShortlisted ? 'fill-[#edc967]' : ''}`} />
                {isShortlisted ? 'Shortlisted' : 'Shortlist'}
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="bg-gold-gradient hover:brightness-110 text-black">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Send Message
                  </Button>
                </DialogTrigger>
                <DialogContent className="role-surface-employer border-white/10 bg-role-employer-overlay text-white backdrop-blur-xl">
                  <DialogHeader>
                    <DialogTitle className="text-white">Send Message to {candidate.name}</DialogTitle>
                    <DialogDescription className="text-gray-400">
                      Your message will be encrypted end-to-end
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <Textarea
                      placeholder="Type your message..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      className="min-h-32 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                    />
                    <Button
                      onClick={sendMessage}
                      className="w-full bg-gold-gradient hover:brightness-110 text-black"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Sidebar - Candidate Summary */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-6">
                <div className="flex flex-col items-center text-center">
                  <Avatar className="w-24 h-24 mb-4">
                    <AvatarFallback className="bg-gradient-to-br from-[#ae8625] to-[#f7ef8a] text-black text-2xl">
                      {candidate.name.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <h2 className="text-white mb-1">{candidate.name}</h2>
                  <p className="text-gray-400 mb-4">{candidate.title}</p>
                  
                  <div className="w-full mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-400">AI Match Score</span>
                      <Badge className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50">
                        {candidate.aiScore}%
                      </Badge>
                    </div>
                    <Progress value={candidate.aiScore} className="h-2" />
                  </div>

                  <Separator className="my-4 bg-white/10" />

                  <div className="w-full space-y-3 text-sm">
                    <div className="flex items-center gap-2 text-gray-300">
                      <MapPin className="w-4 h-4 text-[#ba9036]" />
                      <span>{candidate.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <Briefcase className="w-4 h-4 text-[#ba9036]" />
                      <span>{candidate.experience} experience</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <Clock className="w-4 h-4 text-[#ba9036]" />
                      <span>{candidate.availability}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <DollarSign className="w-4 h-4 text-[#ba9036]" />
                      <span>{candidate.salary}</span>
                    </div>
                  </div>

                  <Separator className="my-4 bg-white/10" />

                  <div className="w-full space-y-2">
                    <Button
                      variant="outline"
                      className="w-full border-white/20 text-white hover:bg-white/5"
                      onClick={downloadCV}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download CV
                    </Button>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <Shield className="w-3 h-3" />
                      <span>Background Check: Verified</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-sm">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-[#ba9036]" />
                  <a href={`mailto:${candidate.email}`} className="text-gray-300 hover:text-[#ba9036] transition-colors">
                    {candidate.email}
                  </a>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-[#ba9036]" />
                  <a href={`tel:${candidate.phone}`} className="text-gray-300 hover:text-[#ba9036] transition-colors">
                    {candidate.phone}
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Work Preferences */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-sm">Work Preferences</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Work Type:</span>
                  <span className="text-white">{candidate.preferences.workType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Shifts:</span>
                  <span className="text-white">{candidate.preferences.shiftPreference}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Notice Period:</span>
                  <span className="text-white">{candidate.preferences.noticePeriod}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Driving License:</span>
                  <span className="text-white">{candidate.preferences.drivingLicense ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Own Transport:</span>
                  <span className="text-white">{candidate.preferences.ownTransport ? 'Yes' : 'No'}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-white/5 border border-white/10 mb-6 w-full justify-start">
                <TabsTrigger value="overview" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
                  Overview
                </TabsTrigger>
                <TabsTrigger value="experience" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
                  Experience
                </TabsTrigger>
                <TabsTrigger value="skills" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
                  Skills & Tests
                </TabsTrigger>
                <TabsTrigger value="references" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
                  References
                </TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6">
                {/* AI Match Analysis */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-[#ba9036]" />
                      AI Match Analysis
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Why this candidate is a great fit for your role
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {candidate.matchReasons.map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-white text-sm">{item.reason}</p>
                          <Progress value={item.score} className="h-1 mt-1" />
                        </div>
                        <Badge variant="outline" className="border-white/20 text-gray-300">
                          {item.score}%
                        </Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Professional Summary */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Professional Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300 leading-relaxed">{candidate.summary}</p>
                  </CardContent>
                </Card>

                {/* Key Skills */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Key Skills</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {candidate.skills.map((skill, index) => (
                        <Badge
                          key={index}
                          className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50 px-3 py-1"
                        >
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Certifications */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Award className="w-5 h-5 text-[#ba9036]" />
                      Certifications & Licenses
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {candidate.certifications.map((cert, index) => (
                      <div key={index} className="flex items-start justify-between p-3 bg-white/5 rounded-lg">
                        <div>
                          <p className="text-white">{cert.name}</p>
                          <p className="text-sm text-gray-400">{cert.issuer}</p>
                        </div>
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                          {cert.expiry}
                        </Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Experience Tab */}
              <TabsContent value="experience" className="space-y-6">
                {/* Work History */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-[#ba9036]" />
                      Work Experience
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {candidate.workHistory.map((job, index) => (
                      <div key={index} className="relative pl-6 pb-6 border-l-2 border-white/10 last:pb-0">
                        <div className="absolute -left-2 top-0 w-4 h-4 rounded-full bg-[#ba9036]" />
                        <div className="mb-2">
                          <h3 className="text-white">{job.role}</h3>
                          <p className="text-[#ba9036] text-sm">{job.company}</p>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {job.duration}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {job.location}
                          </span>
                        </div>
                        <p className="text-gray-300 text-sm">{job.description}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Education */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-[#ba9036]" />
                      Education & Qualifications
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {candidate.education.map((edu, index) => (
                      <div key={index} className="p-4 bg-white/5 rounded-lg">
                        <h3 className="text-white mb-1">{edu.qualification}</h3>
                        <p className="text-sm text-gray-400">{edu.institution}</p>
                        <p className="text-sm text-[#ba9036] mt-1">{edu.year}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Skills & Tests Tab */}
              <TabsContent value="skills" className="space-y-6">
                {/* Test Scores */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Target className="w-5 h-5 text-[#ba9036]" />
                      Skill Assessment Scores
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Completed pre-employment assessments
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {candidate.testScores.map((test, index) => (
                      <div key={index} className="p-4 bg-white/5 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-white">{test.test}</h3>
                          <div className="flex items-center gap-3">
                            <Badge className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50">
                              Score: {test.score}%
                            </Badge>
                            <Badge variant="outline" className="border-white/20 text-gray-300">
                              Top {100 - test.percentile}%
                            </Badge>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Progress value={test.score} className="h-2" />
                          <p className="text-xs text-gray-400">
                            Performed better than {test.percentile}% of test takers
                          </p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Skills Breakdown */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Skills Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      {candidate.skills.map((skill, index) => (
                        <div key={index} className="p-3 bg-white/5 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white text-sm">{skill}</span>
                            <Badge variant="outline" className="border-white/20 text-gray-300 text-xs">
                              Expert
                            </Badge>
                          </div>
                          <Progress value={85 + Math.random() * 15} className="h-1" />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* References Tab */}
              <TabsContent value="references" className="space-y-6">
                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Users className="w-5 h-5 text-[#ba9036]" />
                      Professional References
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Verified references from previous employers
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {candidate.references.map((ref, index) => (
                      <div key={index} className="p-4 bg-white/5 rounded-lg border border-white/10">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-white mb-1">{ref.name}</h3>
                            <p className="text-sm text-gray-400">{ref.role}</p>
                            <p className="text-sm text-[#ba9036]">{ref.company}</p>
                          </div>
                          {ref.available && (
                            <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Available
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <span>Relationship:</span>
                          <span className="text-white">{ref.relationship}</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3 border-white/20 text-white hover:bg-white/5"
                        >
                          <ExternalLink className="w-3 h-3 mr-2" />
                          Request Reference
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Background Checks */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Shield className="w-5 h-5 text-[#ba9036]" />
                      Background Verification
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <span className="text-white">DBS Check</span>
                      </div>
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                        Verified
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <span className="text-white">Identity Verification</span>
                      </div>
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                        Verified
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <span className="text-white">Right to Work</span>
                      </div>
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                        Verified
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
